/**
 * `/liturgies/{id}/exports`.
 *
 * From the mockup's `isExports` block: a left column of Export Profile, Format and Export language,
 * a right column that previews the real export layout, and the sentence that governs the whole
 * feature:
 *
 * > Every export is a snapshot of a planning state, never the authoritative record.
 *
 * **Export language is independent of interface language.** A parish planning in English routinely
 * prints a Spanish worship aid, so it is a control on this screen rather than a session preference
 * (docs/engineering/12-localization.md).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { LaunchLanguage } from '@pius/contracts';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import { fetchLiturgy } from '../liturgies/api';
import {
  exportUrl,
  fetchExports,
  fetchProfiles,
  isRunning,
  requestExport,
  type ExportFormat,
  type ExportRequest,
} from './api';
import './exports.css';

const FORMATS: { value: ExportFormat; label: string; note: string }[] = [
  { value: 'pdf', label: 'PDF — printable', note: 'Rendered by the export service' },
  { value: 'html', label: 'HTML', note: 'The same layout, as a web page' },
  { value: 'text', label: 'Plain text summary', note: 'For an email body' },
  { value: 'ics', label: 'Calendar (ICS)', note: 'The celebration only — never the planning' },
  { value: 'json', label: 'Structured data (JSON)', note: 'The snapshot itself' },
];

const LANGUAGES: { value: LaunchLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
];

export function ExportsScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/exports' });
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const [profileId, setProfileId] = useState<string | null>(null);
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [language, setLanguage] = useState<LaunchLanguage>('en');

  const liturgy = useQuery({ queryKey: ['liturgy', id], queryFn: () => fetchLiturgy(id) });
  const profiles = useQuery({ queryKey: ['export-profiles'], queryFn: fetchProfiles });

  const requests = useQuery({
    queryKey: ['exports', id],
    queryFn: () => fetchExports(id),
    refetchInterval: (query) =>
      query.state.data?.data.some((r) => isRunning(r.state)) ? 2500 : false,
  });

  const generate = useMutation({
    mutationFn: () => requestExport(id, { profile_id: profileId, format, language_code: language }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['exports', id] });
    },
  });

  const error = generate.error instanceof Error ? (generate.error as ApiError) : undefined;

  return (
    <div className="exp">
      <header className="exp__head">
        <div>
          <h1 className="exp__title">{t('exports.title')}</h1>
          <p className="exp__lede">{t('exports.lede')}</p>
        </div>
        <Link className="exp__link" to="/liturgies/$id/planning" params={{ id }}>
          {t('workspace.planning')} →
        </Link>
      </header>

      <WorkspaceTabs liturgyId={id} />

      <div className="exp__grid">
        <div className="exp__controls">
          <section className="exp__card">
            <p className="exp__eyebrow">Export profile</p>
            <div className="exp__choices">
              {(profiles.data?.data ?? []).map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  className={`exp__choice${profileId === profile.id ? ' exp__choice--on' : ''}`}
                  onClick={() => setProfileId(profile.id)}
                >
                  <span className="exp__choice-label">{profile.name}</span>
                  <span className="exp__choice-meta">{profile.sections.length} sections</span>
                </button>
              ))}
              <button
                type="button"
                className={`exp__choice${profileId === null ? ' exp__choice--on' : ''}`}
                onClick={() => setProfileId(null)}
              >
                <span className="exp__choice-label">Everything</span>
                <span className="exp__choice-meta">no profile</span>
              </button>
            </div>
            <p className="exp__fine">
              A profile narrows what a document shows. It cannot reveal anything you could not
              already see — the snapshot is built under your authority before any profile applies.
            </p>
          </section>

          <section className="exp__card">
            <p className="exp__eyebrow">Format &amp; language</p>

            <label className="exp__field">
              <span className="exp__field-label">Format</span>
              <select value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)}>
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <span className="exp__fine">{FORMATS.find((f) => f.value === format)?.note}</span>
            </label>

            <label className="exp__field">
              <span className="exp__field-label">Export language</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as LaunchLanguage)}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
              <span className="exp__fine">Independent of interface and liturgical language.</span>
            </label>

            {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

            <button
              type="button"
              className="exp__btn"
              disabled={generate.isPending}
              onClick={() => generate.mutate()}
            >
              {generate.isPending ? 'Requesting…' : 'Generate export'}
            </button>
          </section>
        </div>

        <div className="exp__history">
          <section className="exp__card">
            <p className="exp__eyebrow">Export history</p>

            {requests.isPending ? (
              <p className="exp__fine">Loading…</p>
            ) : requests.data?.data.length === 0 ? (
              <p className="exp__fine">Nothing has been exported from this celebration yet.</p>
            ) : (
              <ul className="exp__list">
                {(requests.data?.data ?? []).map((row) => (
                  <ExportRow key={row.id} liturgyId={id} row={row} />
                ))}
              </ul>
            )}
          </section>

          <section className="exp__card exp__card--warm">
            <p className="exp__eyebrow">What an export is</p>
            <p className="exp__body">
              A snapshot taken when you asked for it, not when it finished rendering. Two people
              asking a minute apart would otherwise receive different documents because an approval
              landed in between, and neither could say which planning state their copy reflects.
            </p>
            <p className="exp__body">
              {liturgy.data && liturgy.data.lifecycle_state !== 'finalized'
                ? 'This liturgy is not finalized, so every export is stamped as a draft and unapproved items are marked.'
                : 'This liturgy is finalized. Exports print without a draft stamp.'}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function ExportRow({
  liturgyId,
  row,
}: {
  liturgyId: string;
  row: ExportRequest;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <li className="exp__row">
      <div>
        <div className="exp__row-name">
          {row.profile_name ?? 'Everything'} · {row.format.toUpperCase()}
        </div>
        <div className="exp__row-meta">
          {row.language_code} · {row.created_at.slice(0, 16).replace('T', ' ')}
          {row.byte_size ? ` · ${Math.max(1, Math.round(row.byte_size / 1024))} KB` : ''}
        </div>
        {row.failure_reason ? (
          <div className="exp__row-error">This export failed: {row.failure_reason}</div>
        ) : null}
      </div>

      <span className={`exp-pill exp-pill--${row.state}`}>
        {row.state === 'rendering' ? 'Rendering' : row.state}
      </span>

      {/* No control at all unless the file is genuinely available — an expired artifact is gone,
          and offering a dead link would be worse than offering none. */}
      {row.available ? (
        <a className="exp__btn exp__btn--small" href={exportUrl(liturgyId, row.id)} download>
          {t('exports.download')}
        </a>
      ) : (
        <span />
      )}
    </li>
  );
}
